import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Box, Stack, Button, CircularProgress } from "@mui/material";
import { ParentContainer } from "../components/shared/sharedVisualElements";
import { getRandomFXData, postAttemptData } from "../apis/APICalls";
import { useApiCall } from '../hooks/useApiCall';
import { useSettings } from '../stateManagement/SettingsContext';
import { FinancialCandlestickChart } from "../components/CandlestickElements/Finance";
import { TradeButtons } from "../components/TradeButtons";
import SettingsModal from "../pages/settings";
import WelcomeOnboarding, { WELCOME_STORAGE_KEY } from "../components/WelcomeOnboarding";
import TutorialOverlay from "../components/TutorialOverlay";

export const HomePage=()=>{
    const location = useLocation();
    const navigate = useNavigate();
    const { settings, updateSetting, isLoading: settingsLoading, markTutorialCompleted, loadSettings } = useSettings();
    const { setting_time_frame: timeFrame, setting_periods_back: periodsBack, setting_periods_forward: periodsForward, setting_take_profit: takeProfit, setting_stop_loss: stopLoss, setting_spread: spread } = settings;
    const generateDummyCandles = (count = 50, startPrice = 1.1) => {
        const candles = [];
        let last = startPrice;
        for (let i = 0; i < count; i++) {
            const drift = (Math.random() - 0.5) * 0.004; // small random walk
            const open = last;
            const close = Math.max(0.5, open + drift);
            const high = Math.max(open, close) + Math.random() * 0.0015;
            const low = Math.min(open, close) - Math.random() * 0.0015;
            const id = 1000 + i; // arbitrary ids for axis
            candles.push({
                open: Number(open.toFixed(5)),
                high: Number(high.toFixed(5)),
                low: Number(low.toFixed(5)),
                close: Number(close.toFixed(5)),
                first_id: id,
                last_id: id
            });
            last = close;
        }
        return candles;
    };

    const [candleData, setCandleData] = useState(() => generateDummyCandles(50)); 
    const [pl, setPL] = useState(null);
    const [lastTickID, setLastTickID] = useState(0);
    const { executeApiCall, isLoading: apiLoading } = useApiCall();
    const [showWelcome, setShowWelcome] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [forceOpenSettings, setForceOpenSettings] = useState(false);
    const [isStarted, setIsStarted] = useState(false);

    const hasCompletedOnboarding = useMemo(() => {
        try {
            return localStorage.getItem(WELCOME_STORAGE_KEY) === 'true';
        } catch (e) {
            return false;
        }
    }, []);

    useEffect(() => {
        if (!hasCompletedOnboarding) {
            setShowWelcome(true);
        }
    }, [hasCompletedOnboarding]);

    useEffect(() => {
        if (location && location.hash === '#settings') {
            setForceOpenSettings(true);
            try {
                navigate('/', { replace: true });
            } catch (e) {}
        }
    }, [location, navigate]);

    // Do not auto-fetch data on load; wait for user to start (session-optimized)
    useEffect(() => { 
        // If session previously started in this tab, auto-start once
        try {
            if (sessionStorage.getItem('tv_session_started') === 'true' && !isStarted && !settingsLoading) {
                setIsStarted(true);
                // Ensure settings are loaded
                loadSettings();
                getRandomData();
            }
        } catch (e) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading]);

    useEffect(() => {
        let staleTimeout;
        if (lastTickID > 0) {
            staleTimeout = setTimeout(() => {
                getRandomData();
            }, 2000);
        }
        return () => { if (staleTimeout) clearTimeout(staleTimeout); };
    }, [lastTickID]);

    const getRandomData = async ()=>{
        await executeApiCall(() => getRandomFXData(timeFrame, periodsBack),
        {
            onSuccess: (response) => {
                setCandleData(response);
                setPL(null);
                setLastTickID(0);
            }
        }
        );} 
    
    const sendAttempt = async (buy_sell)=>{
        if (!candleData || candleData.length === 0) return;
        const requestionOptions = {
            buy_sell: buy_sell,
            tp: takeProfit,
            sl: stopLoss,
            first_id: candleData[0]?.first_id,
            final_tick: candleData[candleData.length - 1]?.last_id,
            periods_back: periodsBack,
            periods_forward: periodsForward,
            spread: spread,
            time_frame: timeFrame
            };
        if (!requestionOptions.first_id || !requestionOptions.final_tick) {
            console.error("Missing first_id or final_tick in candleData");
            return; 
        }
        await executeApiCall(() => postAttemptData(requestionOptions),
        {
            onSuccess: (response) => {
                setCandleData(response.fullFXData);
                setPL(response.pl);
                setLastTickID(response.final_tick_id);
            }
        }
        );
    }

    if (settingsLoading) {
        return <Typography>Loading Settings...</Typography>;
    }

    return(
        <ParentContainer>
            <WelcomeOnboarding
                open={showWelcome}
                onStartTutorial={() => {
                    try { localStorage.setItem(WELCOME_STORAGE_KEY, 'true'); } catch (e) {}
                    setShowWelcome(false);
                    setShowTutorial(true);
                }}
                onSkip={() => {
                    try { localStorage.setItem(WELCOME_STORAGE_KEY, 'true'); } catch (e) {}
                    setShowWelcome(false);
                }}
            />
            <TutorialOverlay
                open={showTutorial}
                onClose={() => { setShowTutorial(false); getRandomData(); }}
                onStepChange={(i) => {
                    // Step 4 (index 3) shows green; other steps revert to neutral
                    if (i === 3) {
                        setPL(1);
                    } else {
                        setPL(null);
                    }
                    // Auto-open Settings for settings-related steps
                    const settingsStepStart = 5; // index where settings steps begin
                    setForceOpenSettings(i >= settingsStepStart);
                }}
                onComplete={() => {
                    markTutorialCompleted();
                    setForceOpenSettings(false);
                }}
                steps={[
                    { selector: '#tv-chart', content: 'This is a forex candlestick chart showing price movement over time' },
                    { selector: '#tv-chart', content: 'Look for patterns like trends, support/resistance, or breakouts' },
                    { selector: '#tv-trade-buttons', content: 'Click BUY if you think price will go up, SELL if you think it will go down' },
                    { selector: '#tv-chart', content: "You'll get instant feedback and see the actual outcome" },
                    { selector: '#tv-trade-buttons', content: 'Practice many trades quickly to develop pattern recognition skills' },
                    { selector: '#tv-settings-button', content: 'Open Settings to customize how scenarios are generated and evaluated' },
                    { selector: '#tv-setting-spread', content: 'Spread: simulated cost of trading in pips; higher spread reduces net P/L' },
                    { selector: '#tv-setting-timeframe', content: 'Time Frame: resolution of candles (e.g., 1min, 5min). Choose what you want to train.' },
                    { selector: '#tv-setting-periods-back', content: 'Periods Back: how many candles are shown before the decision point' },
                    { selector: '#tv-setting-periods-forward', content: 'Periods Forward: how many candles are revealed for the outcome' },
                    { selector: '#tv-setting-tp', content: 'Take Profit: pips target; closes trade when reached to lock gains' },
                    { selector: '#tv-setting-sl', content: 'Stop Loss: pips risk; closes trade when reached to cap losses' }
                ]}
            />
            <SettingsModal 
                settings={settings}
                updateSetting={updateSetting}
                onRequestOnboarding={() => setShowWelcome(true)}
                onRequestTutorial={() => setShowTutorial(true)}
                forceOpen={forceOpenSettings}
                onForceClose={() => setForceOpenSettings(false)}
             />
            <Box sx={{ position: 'relative', width: '100%' }}>
                {/* Chart always rendered; optionally greyed out */}
                <Box sx={{ filter: !isStarted ? 'grayscale(100%) brightness(0.9)' : 'none', pointerEvents: !isStarted ? 'none' : 'auto' }}>
                    <PageTitle timeFrame={timeFrame} showTimeframe={!!settings.setting_show_timeframe_label}/>
                    <FinancialCandlestickChart 
                        data={candleData}
                        showToolTip={false}
                        pl={pl}
                        lastTickID={lastTickID}
                        isLoading={apiLoading}
                        showPriceScale={!!settings.setting_show_price_scale}
                        showTimeScale={!!settings.setting_show_time_scale}
                        showCurrentPriceLine={!!settings.setting_show_current_price_line}
                    />
                    {!forceOpenSettings && isStarted && (
                        <TradeButtons getRandomData={getRandomData} sendAttempt={sendAttempt} isLoading={apiLoading}/>
                    )}
                </Box>

                {!isStarted && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Stack spacing={2} alignItems="center" sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>Ready to train?</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button variant="contained" color="primary" onClick={() => { setIsStarted(true); try { sessionStorage.setItem('tv_session_started', 'true'); } catch (e) {}; loadSettings(); getRandomData(); }}>Start Trading</Button>
                                <Button variant="outlined" color="primary" onClick={() => { setIsStarted(true); setShowWelcome(true); try { sessionStorage.setItem('tv_session_started', 'true'); } catch (e) {}; loadSettings(); getRandomData(); }}>View Tutorial</Button>
                            </Stack>
                            {apiLoading && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">Preparing your first scenario…</Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Box>
                )}
            </Box>
            
        </ParentContainer>);
}

const PageTitle=({ timeFrame, showTimeframe })=>{
    return(
    <Typography>
        {showTimeframe ? `Timeframe: ${timeFrame}` : ''}
    </Typography>
    );
}